import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "./CommunityEditableGrid.css";

const ROW_NUMBER_COLUMN_ID = "__flitRowNumber";
const ORDER_ID_COLUMN_ID = "orderIdLabel";
const ORDER_ID_MIN_WIDTH = 90;

function getOrderIdColumnWidth(rowData, columnDef) {
  const values = [
    columnDef.headerName || "",
    ...(rowData || []).map(row => row?.[columnDef.field] ?? ""),
  ].map(value => String(value));

  if (typeof document === "undefined") {
    return Math.max(
      ORDER_ID_MIN_WIDTH,
      ...values.map(value => value.length * 8 + 32)
    );
  }

  const context = document.createElement("canvas").getContext("2d");
  if (!context) return ORDER_ID_MIN_WIDTH;
  context.font = '12px Poppins, Helvetica, sans-serif';
  const contentWidth = Math.max(...values.map(value => context.measureText(value).width));
  return Math.max(ORDER_ID_MIN_WIDTH, Math.ceil(contentWidth + 32));
}

function getDataColumns(columnApi) {
  return columnApi
    .getAllDisplayedColumns()
    .filter(column => column.getColId() !== ROW_NUMBER_COLUMN_ID);
}

function SelectAllHeader({ onSelectAll }) {
  return (
    <button
      type="button"
      className="community-select-all-button"
      aria-label="Select all table cells"
      title="Select all table cells"
      onClick={onSelectAll}
    >
      <span className="community-select-all-corner" aria-hidden="true" />
    </button>
  );
}

function isEditable(column, rowNode, api, columnApi) {
  const colDef = column.getColDef();
  if (typeof colDef.editable !== "function") return Boolean(colDef.editable);
  return Boolean(colDef.editable({
    api,
    columnApi,
    colDef,
    column,
    data: rowNode.data,
    node: rowNode,
    rowIndex: rowNode.rowIndex,
  }));
}

function parseValue(column, rawValue, rowNode, api, columnApi) {
  const colDef = column.getColDef();
  if (typeof colDef.valueParser !== "function") return rawValue;
  return colDef.valueParser({
    api,
    columnApi,
    colDef,
    column,
    data: rowNode.data,
    node: rowNode,
    oldValue: rowNode.data?.[colDef.field],
    newValue: rawValue,
  });
}

function isEditorTarget(target) {
  return target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target?.isContentEditable;
}

function getRangeBounds(api, columnApi, range) {
  if (!range) return null;
  const columns = columnApi.getAllDisplayedColumns();
  const startColumn = columns.findIndex(column => column.getColId() === range.start.columnId);
  const endColumn = columns.findIndex(column => column.getColId() === range.end.columnId);
  if (startColumn < 0 || endColumn < 0) return null;
  return {
    columns,
    firstColumn: Math.min(startColumn, endColumn),
    lastColumn: Math.max(startColumn, endColumn),
    firstRow: Math.min(range.start.rowIndex, range.end.rowIndex),
    lastRow: Math.max(range.start.rowIndex, range.end.rowIndex),
    api,
  };
}

function formatCopyValue(column, rowNode, api, columnApi) {
  const colDef = column.getColDef();
  const value = api.getValue(column, rowNode);
  const formattedValue = typeof colDef.valueFormatter === "function"
    ? colDef.valueFormatter({
      api,
      columnApi,
      colDef,
      column,
      data: rowNode.data,
      node: rowNode,
      value,
    })
    : value;
  return (formattedValue ?? "").toString().replace(/[\t\r\n]+/g, " ");
}

export default function CommunityEditableGrid({
  columnDefs,
  defaultColDef,
  rowData,
  onCellFocused,
  onCellMouseDown,
  onCellMouseOver,
  onGridReady,
  onBodyScroll,
  ...gridProps
}) {
  const gridRef = useRef(null);
  const gridContainerRef = useRef(null);
  const gridParamsRef = useRef(null);
  const copyRangeRef = useRef(null);
  const isSelectingRef = useRef(false);
  const isHeaderSelectingRef = useRef(false);
  const preservedFocusRef = useRef(null);
  const copyNoticeTimeoutRef = useRef(null);
  const rangeRefreshFrameRef = useRef(null);
  const selectionOutlineRef = useRef(null);
  const [copyNotice, setCopyNotice] = useState("");

  useEffect(() => {
    const stopSelecting = () => {
      isSelectingRef.current = false;
      isHeaderSelectingRef.current = false;
      document.body.classList.remove("community-column-selecting");
    };
    document.addEventListener("mouseup", stopSelecting);
    window.addEventListener("blur", stopSelecting);
    return () => {
      document.removeEventListener("mouseup", stopSelecting);
      window.removeEventListener("blur", stopSelecting);
      document.body.classList.remove("community-column-selecting");
      if (copyNoticeTimeoutRef.current) clearTimeout(copyNoticeTimeoutRef.current);
      if (rangeRefreshFrameRef.current !== null) {
        window.cancelAnimationFrame(rangeRefreshFrameRef.current);
      }
    };
  }, []);

  const handleGridReady = useCallback(params => {
    gridParamsRef.current = params;
    if (onGridReady) onGridReady(params);
  }, [onGridReady]);

  const updateSelectionOutline = useCallback(() => {
    const container = gridContainerRef.current;
    const outline = selectionOutlineRef.current;
    const params = gridParamsRef.current;
    const bounds = params
      ? getRangeBounds(params.api, params.columnApi, copyRangeRef.current)
      : null;
    if (!container || !outline || !bounds) {
      if (outline) outline.style.opacity = "0";
      return;
    }

    const body = container.querySelector(".ag-body-viewport");
    const bodyRect = body?.getBoundingClientRect();
    const selectedRects = Array.from(container.querySelectorAll(".ag-cell[col-id]"))
      .filter(cell => {
        const rowIndex = Number(cell.closest(".ag-row")?.getAttribute("row-index"));
        const columnId = cell.getAttribute("col-id");
        const columnIndex = bounds.columns.findIndex(
          column => column.getColId() === columnId
        );
        return Number.isInteger(rowIndex)
          && rowIndex >= bounds.firstRow
          && rowIndex <= bounds.lastRow
          && columnIndex >= bounds.firstColumn
          && columnIndex <= bounds.lastColumn;
      })
      .map(cell => cell.getBoundingClientRect())
      .filter(rect => rect.width > 0
        && rect.height > 0
        && (!bodyRect || (rect.right > bodyRect.left
          && rect.left < bodyRect.right
          && rect.bottom > bodyRect.top
          && rect.top < bodyRect.bottom)));

    if (selectedRects.length === 0) {
      outline.style.opacity = "0";
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const left = Math.max(
      Math.min(...selectedRects.map(rect => rect.left)),
      bodyRect?.left ?? -Infinity
    );
    const right = Math.min(
      Math.max(...selectedRects.map(rect => rect.right)),
      bodyRect?.right ?? Infinity
    );
    const top = Math.max(
      Math.min(...selectedRects.map(rect => rect.top)),
      bodyRect?.top ?? -Infinity
    );
    const bottom = Math.min(
      Math.max(...selectedRects.map(rect => rect.bottom)),
      bodyRect?.bottom ?? Infinity
    );

    outline.style.transform = "translate3d(" + (left - containerRect.left)
      + "px, " + (top - containerRect.top) + "px, 0)";
    outline.style.width = Math.max(0, right - left) + "px";
    outline.style.height = Math.max(0, bottom - top) + "px";
    outline.style.opacity = "1";
  }, []);

  const refreshRange = useCallback(() => {
    if (rangeRefreshFrameRef.current !== null) return;
    rangeRefreshFrameRef.current = window.requestAnimationFrame(() => {
      rangeRefreshFrameRef.current = null;
      if (gridParamsRef.current) {
        gridParamsRef.current.api.refreshCells({ force: true });
        gridParamsRef.current.api.refreshHeader();
      }
      updateSelectionOutline();
    });
  }, [updateSelectionOutline]);

  const handleBodyScroll = useCallback(event => {
    refreshRange();
    if (onBodyScroll) onBodyScroll(event);
  }, [onBodyScroll, refreshRange]);

  useEffect(() => {
    const clearSelectionOutsideGrid = event => {
      if (gridContainerRef.current?.contains(event.target)) return;
      copyRangeRef.current = null;
      if (gridParamsRef.current) gridParamsRef.current.api.clearFocusedCell();
      refreshRange();
    };

    document.addEventListener("mousedown", clearSelectionOutsideGrid);
    return () => document.removeEventListener("mousedown", clearSelectionOutsideGrid);
  }, [refreshRange]);

  const focusCellPreservingRange = useCallback((api, rowIndex, column) => {
    preservedFocusRef.current = { rowIndex, columnId: column.getColId() };
    api.setFocusedCell(rowIndex, column);
  }, []);

  const handleSelectAll = useCallback(() => {
    const params = gridParamsRef.current;
    if (!params) return;
    const columns = getDataColumns(params.columnApi);
    const rowCount = params.api.getDisplayedRowCount();
    if (columns.length === 0 || rowCount === 0) return;

    copyRangeRef.current = {
      start: { rowIndex: 0, columnId: columns[0].getColId() },
      end: { rowIndex: rowCount - 1, columnId: columns[columns.length - 1].getColId() },
      selectionMode: "all",
    };
    focusCellPreservingRange(params.api, 0, columns[0]);
    refreshRange();
  }, [focusCellPreservingRange, refreshRange]);

  const getHeaderColumnFromTarget = useCallback(target => {
    const params = gridParamsRef.current;
    if (!params || !(target instanceof Element)) return null;
    if (target.closest(".community-select-all-button")) return null;
    if (target.closest(".ag-header-cell-resize, .ag-header-cell-menu-button")) return null;
    const columnId = target.closest(".ag-header-cell")?.getAttribute("col-id");
    if (!columnId || columnId === ROW_NUMBER_COLUMN_ID) return null;
    return params.columnApi.getColumn(columnId);
  }, []);

  const getGridColumnFromTarget = useCallback(target => {
    const params = gridParamsRef.current;
    if (!params || !(target instanceof Element)) return null;
    const columnId = target
      .closest(".ag-header-cell, .ag-cell")
      ?.getAttribute("col-id");
    if (!columnId || columnId === ROW_NUMBER_COLUMN_ID) return null;
    return params.columnApi.getColumn(columnId);
  }, []);

  const getGridColumnFromClientX = useCallback(clientX => {
    const params = gridParamsRef.current;
    const container = gridContainerRef.current;
    if (!params || !container) return null;

    const candidates = Array.from(container.querySelectorAll(".ag-header-cell[col-id]"))
      .map(element => ({
        columnId: element.getAttribute("col-id"),
        rect: element.getBoundingClientRect(),
      }))
      .filter(candidate => candidate.columnId
        && candidate.columnId !== ROW_NUMBER_COLUMN_ID
        && candidate.rect.width > 0);
    if (candidates.length === 0) return null;

    const nearest = candidates.reduce((best, candidate) => {
      const distance = clientX < candidate.rect.left
        ? candidate.rect.left - clientX
        : clientX > candidate.rect.right ? clientX - candidate.rect.right : 0;
      return !best || distance < best.distance ? { ...candidate, distance } : best;
    }, null);
    return params.columnApi.getColumn(nearest.columnId);
  }, []);

  const getGridRowIndexFromClientY = useCallback(clientY => {
    const container = gridContainerRef.current;
    if (!container) return null;

    const bodyRect = container.querySelector(".ag-body-viewport")?.getBoundingClientRect();
    const candidates = Array.from(
      container.querySelectorAll(".ag-center-cols-container .ag-row[row-index]")
    )
      .map(element => ({
        rowIndex: Number(element.getAttribute("row-index")),
        rect: element.getBoundingClientRect(),
      }))
      .filter(candidate => Number.isInteger(candidate.rowIndex)
        && candidate.rect.height > 0
        && (!bodyRect || (candidate.rect.bottom > bodyRect.top
          && candidate.rect.top < bodyRect.bottom)));
    if (candidates.length === 0) return null;

    const nearest = candidates.reduce((best, candidate) => {
      const distance = clientY < candidate.rect.top
        ? candidate.rect.top - clientY
        : clientY > candidate.rect.bottom ? clientY - candidate.rect.bottom : 0;
      return !best || distance < best.distance ? { ...candidate, distance } : best;
    }, null);
    return nearest.rowIndex;
  }, []);

  const extendHeaderSelection = useCallback(column => {
    const params = gridParamsRef.current;
    const range = copyRangeRef.current;
    if (!params || !column || !range) return;
    const columnId = column.getColId();
    if (range.end.columnId === columnId) return;

    copyRangeRef.current = {
      ...range,
      end: {
        rowIndex: params.api.getDisplayedRowCount() - 1,
        columnId,
      },
    };
    refreshRange();
  }, [refreshRange]);

  const handleGridMouseDown = useCallback(event => {
    if (event.button !== 0) return;
    const params = gridParamsRef.current;
    const column = getHeaderColumnFromTarget(event.target);
    const rowCount = params?.api.getDisplayedRowCount() || 0;
    if (!params || !column || rowCount === 0) return;

    event.preventDefault();
    event.stopPropagation();
    const columnId = column.getColId();
    copyRangeRef.current = {
      start: { rowIndex: 0, columnId },
      end: { rowIndex: rowCount - 1, columnId },
      selectionMode: "column",
    };
    isHeaderSelectingRef.current = true;
    document.body.classList.add("community-column-selecting");
    focusCellPreservingRange(params.api, 0, column);
    refreshRange();
  }, [focusCellPreservingRange, getHeaderColumnFromTarget, refreshRange]);

  const handleGridMouseOver = useCallback(event => {
    if (!isHeaderSelectingRef.current) return;
    if ((event.buttons & 1) === 0) {
      isHeaderSelectingRef.current = false;
      return;
    }

    const params = gridParamsRef.current;
    const column = getGridColumnFromTarget(event.target);
    if (!params || !column || !copyRangeRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    extendHeaderSelection(column);
  }, [extendHeaderSelection, getGridColumnFromTarget]);

  useEffect(() => {
    const handleDocumentMouseMove = event => {
      const isHeaderSelecting = isHeaderSelectingRef.current;
      const isCellSelecting = isSelectingRef.current;
      if (!isHeaderSelecting && !isCellSelecting) return;

      if ((event.buttons & 1) === 0) {
        isHeaderSelectingRef.current = false;
        isSelectingRef.current = false;
        document.body.classList.remove("community-column-selecting");
        return;
      }

      const isOverGridCell = event.target instanceof Element
        && gridContainerRef.current?.contains(event.target)
        && Boolean(event.target.closest(".ag-cell"));
      if (isOverGridCell) return;

      event.preventDefault();
      if (isHeaderSelecting) {
        extendHeaderSelection(getGridColumnFromClientX(event.clientX));
        return;
      }

      const params = gridParamsRef.current;
      const range = copyRangeRef.current;
      const rowIndex = getGridRowIndexFromClientY(event.clientY);
      if (!params || !range || rowIndex === null) return;

      if (range.selectionMode === "rows") {
        const columns = getDataColumns(params.columnApi);
        if (columns.length === 0) return;
        copyRangeRef.current = {
          ...range,
          end: { rowIndex, columnId: columns[columns.length - 1].getColId() },
        };
      } else {
        const column = getGridColumnFromClientX(event.clientX);
        if (!column) return;
        copyRangeRef.current = {
          ...range,
          end: { rowIndex, columnId: column.getColId() },
        };
      }
      refreshRange();
    };

    document.addEventListener("mousemove", handleDocumentMouseMove);
    return () => document.removeEventListener("mousemove", handleDocumentMouseMove);
  }, [
    extendHeaderSelection,
    getGridColumnFromClientX,
    getGridRowIndexFromClientY,
    refreshRange,
  ]);

  const rowNumberColumn = useMemo(() => ({
    colId: ROW_NUMBER_COLUMN_ID,
    headerName: "",
    headerComponent: SelectAllHeader,
    headerComponentParams: { onSelectAll: handleSelectAll },
    headerClass: "community-row-number-header",
    cellClass: "community-row-number-cell",
    cellClassRules: {
      "community-row-number-selected": cellParams => {
        const bounds = getRangeBounds(cellParams.api, cellParams.columnApi, copyRangeRef.current);
        return Boolean(bounds
          && cellParams.node.rowIndex >= bounds.firstRow
          && cellParams.node.rowIndex <= bounds.lastRow);
      },
    },
    valueGetter: params => params.node.rowIndex + 1,
    width: 38,
    minWidth: 38,
    maxWidth: 38,
    pinned: "left",
    lockPinned: true,
    lockPosition: true,
    suppressMovable: true,
    suppressNavigable: true,
    suppressMenu: true,
    sortable: false,
    filter: false,
    editable: false,
    resizable: false,
  }), [handleSelectAll]);

  const enhancedColumnDefs = useMemo(() => {
    const dataColumns = (columnDefs || []).map(column => {
      const columnId = column.colId || column.field;
      if (columnId !== ORDER_ID_COLUMN_ID) return column;
      const width = getOrderIdColumnWidth(rowData, column);
      return {
        ...column,
        width,
        minWidth: width,
      };
    });
    return [rowNumberColumn, ...dataColumns];
  }, [columnDefs, rowData, rowNumberColumn]);

  const handleCellMouseDown = useCallback(event => {
    const mouseEvent = event.event;
    if (mouseEvent && mouseEvent.button !== 0) {
      if (onCellMouseDown) onCellMouseDown(event);
      return;
    }
    if (event.column.getColId() === ROW_NUMBER_COLUMN_ID) {
      const columns = getDataColumns(event.columnApi);
      if (columns.length > 0) {
        const existingRange = copyRangeRef.current;
        const anchorRowIndex = mouseEvent?.shiftKey
          && existingRange?.selectionMode === "rows"
          ? existingRange.start.rowIndex
          : event.rowIndex;
        copyRangeRef.current = {
          start: { rowIndex: anchorRowIndex, columnId: columns[0].getColId() },
          end: { rowIndex: event.rowIndex, columnId: columns[columns.length - 1].getColId() },
          selectionMode: "rows",
        };
        isSelectingRef.current = true;
        focusCellPreservingRange(event.api, event.rowIndex, columns[0]);
        refreshRange();
      }
      if (onCellMouseDown) onCellMouseDown(event);
      return;
    }

    const position = { rowIndex: event.rowIndex, columnId: event.column.getColId() };
    const existingRange = copyRangeRef.current;
    copyRangeRef.current = {
      start: mouseEvent?.shiftKey && existingRange ? existingRange.start : position,
      end: position,
    };
    isSelectingRef.current = true;
    refreshRange();
    if (onCellMouseDown) onCellMouseDown(event);
  }, [focusCellPreservingRange, onCellMouseDown, refreshRange]);

  const handleCellMouseOver = useCallback(event => {
    if (isSelectingRef.current) {
      if (event.event && (event.event.buttons & 1) === 0) {
        isSelectingRef.current = false;
      } else if (copyRangeRef.current) {
        if (copyRangeRef.current.selectionMode === "rows") {
          const columns = getDataColumns(event.columnApi);
          if (columns.length > 0) {
            copyRangeRef.current = {
              ...copyRangeRef.current,
              end: { rowIndex: event.rowIndex, columnId: columns[columns.length - 1].getColId() },
            };
          }
        } else {
          copyRangeRef.current = {
            ...copyRangeRef.current,
            end: { rowIndex: event.rowIndex, columnId: event.column.getColId() },
          };
        }
        refreshRange();
      }
    }
    if (onCellMouseOver) onCellMouseOver(event);
  }, [onCellMouseOver, refreshRange]);

  const getCellCopyRangePosition = useCallback(cellParams => {
    const bounds = getRangeBounds(cellParams.api, cellParams.columnApi, copyRangeRef.current);
    if (!bounds) return null;
    const columnIndex = bounds.columns.findIndex(column => column === cellParams.column);
    const rowIndex = cellParams.node.rowIndex;
    const isInside = rowIndex >= bounds.firstRow
      && rowIndex <= bounds.lastRow
      && columnIndex >= bounds.firstColumn
      && columnIndex <= bounds.lastColumn;
    if (!isInside) return null;
    return {
      top: rowIndex === bounds.firstRow,
      right: columnIndex === bounds.lastColumn,
      bottom: rowIndex === bounds.lastRow,
      left: columnIndex === bounds.firstColumn,
    };
  }, []);

  const enhancedDefaultColDef = useMemo(() => ({
    ...defaultColDef,
    suppressMovable: true,
    headerClass: headerParams => {
      const configuredClass = typeof defaultColDef?.headerClass === "function"
        ? defaultColDef.headerClass(headerParams)
        : defaultColDef?.headerClass;
      const classes = Array.isArray(configuredClass)
        ? configuredClass
        : configuredClass ? [configuredClass] : [];
      const range = copyRangeRef.current;
      if (range?.selectionMode === "column" || range?.selectionMode === "all") {
        const bounds = getRangeBounds(headerParams.api, headerParams.columnApi, range);
        const columnIndex = bounds?.columns.findIndex(column => column === headerParams.column);
        if (bounds && columnIndex >= bounds.firstColumn && columnIndex <= bounds.lastColumn) {
          classes.push("community-copy-header-selected");
        }
      }
      return classes;
    },
    cellClassRules: {
      ...defaultColDef?.cellClassRules,
      "community-read-only-cell": params => !isEditable(
        params.column,
        params.node,
        params.api,
        params.columnApi
      ),
      "community-copy-range": params => Boolean(getCellCopyRangePosition(params)),
      "community-copy-range-top": params => Boolean(getCellCopyRangePosition(params)?.top),
      "community-copy-range-right": params => Boolean(getCellCopyRangePosition(params)?.right),
      "community-copy-range-bottom": params => Boolean(getCellCopyRangePosition(params)?.bottom),
      "community-copy-range-left": params => Boolean(getCellCopyRangePosition(params)?.left),
    },
  }), [defaultColDef, getCellCopyRangePosition]);

  const clearSelectedCells = useCallback(() => {
    const params = gridParamsRef.current;
    const bounds = params
      ? getRangeBounds(params.api, params.columnApi, copyRangeRef.current)
      : null;
    if (!params || !bounds) return 0;

    let clearedCellCount = 0;
    for (let rowIndex = bounds.firstRow; rowIndex <= bounds.lastRow; rowIndex += 1) {
      const rowNode = params.api.getDisplayedRowAtIndex(rowIndex);
      if (!rowNode) continue;
      const selectedColumns = bounds.columns.slice(
        bounds.firstColumn,
        bounds.lastColumn + 1
      );
      for (let columnIndex = 0; columnIndex < selectedColumns.length; columnIndex += 1) {
        const column = selectedColumns[columnIndex];
        if (!isEditable(column, rowNode, params.api, params.columnApi)) continue;
        const value = parseValue(column, "", rowNode, params.api, params.columnApi);
        rowNode.setDataValue(column, value, "rangeClear");
        clearedCellCount += 1;
      }
    }
    params.api.refreshCells({ force: true });
    return clearedCellCount;
  }, []);

  const handleGridKeyDown = useCallback(event => {
    const params = gridParamsRef.current;
    const target = event.target;
    const isEditing = params?.api.getEditingCells().length > 0
      || isEditorTarget(target)
      || (target instanceof Element && Boolean(target.closest(".ag-cell-inline-editing")));
    if (!params || isEditing) return;

    const modifierKey = event.ctrlKey || event.metaKey;
    const normalizedKey = event.key.toLowerCase();
    if (modifierKey && normalizedKey === "a" && !event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      handleSelectAll();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      copyRangeRef.current = null;
      params.api.clearFocusedCell();
      refreshRange();
      return;
    }

    if (!modifierKey && (event.key === "Delete" || event.key === "Backspace")) {
      event.preventDefault();
      event.stopPropagation();
      clearSelectedCells();
      return;
    }

    const direction = {
      ArrowUp: { row: -1, column: 0 },
      ArrowRight: { row: 0, column: 1 },
      ArrowDown: { row: 1, column: 0 },
      ArrowLeft: { row: 0, column: -1 },
    }[event.key];
    if (!direction || event.altKey) return;

    const focusedCell = params.api.getFocusedCell();
    const columns = getDataColumns(params.columnApi);
    const rowCount = params.api.getDisplayedRowCount();
    if (!focusedCell || columns.length === 0 || rowCount === 0) return;

    const existingRange = copyRangeRef.current;
    const extendExistingRange = event.shiftKey && existingRange && !existingRange.selectionMode;
    const activePosition = extendExistingRange ? existingRange.end : {
      rowIndex: focusedCell.rowIndex,
      columnId: focusedCell.column.getColId(),
    };
    const activeColumnIndex = columns.findIndex(
      column => column.getColId() === activePosition.columnId
    );
    if (activeColumnIndex < 0) return;

    const jumpToEdge = modifierKey;
    const nextRowIndex = jumpToEdge && direction.row !== 0
      ? direction.row < 0 ? 0 : rowCount - 1
      : Math.max(0, Math.min(rowCount - 1, activePosition.rowIndex + direction.row));
    const nextColumnIndex = jumpToEdge && direction.column !== 0
      ? direction.column < 0 ? 0 : columns.length - 1
      : Math.max(0, Math.min(columns.length - 1, activeColumnIndex + direction.column));
    const nextColumn = columns[nextColumnIndex];
    const nextPosition = { rowIndex: nextRowIndex, columnId: nextColumn.getColId() };
    const anchor = extendExistingRange ? existingRange.start : {
      rowIndex: focusedCell.rowIndex,
      columnId: focusedCell.column.getColId(),
    };

    event.preventDefault();
    event.stopPropagation();
    copyRangeRef.current = event.shiftKey
      ? { start: anchor, end: nextPosition }
      : { start: nextPosition, end: nextPosition };
    focusCellPreservingRange(params.api, nextRowIndex, nextColumn);
    params.api.ensureIndexVisible(nextRowIndex);
    params.api.ensureColumnVisible(nextColumn);
    refreshRange();
  }, [clearSelectedCells, focusCellPreservingRange, handleSelectAll, refreshRange]);

  const handleCellFocused = useCallback(event => {
    const expectedFocus = preservedFocusRef.current;
    if (expectedFocus) {
      preservedFocusRef.current = null;
      if (event.rowIndex === expectedFocus.rowIndex
        && event.column?.getColId() === expectedFocus.columnId) {
        if (onCellFocused) onCellFocused(event);
        return;
      }
    }

    if (event.rowIndex !== null
      && event.rowIndex !== undefined
      && event.column
      && event.column.getColId() !== ROW_NUMBER_COLUMN_ID) {
      const position = {
        rowIndex: event.rowIndex,
        columnId: event.column.getColId(),
      };
      copyRangeRef.current = { start: position, end: position };
      refreshRange();
    }
    if (onCellFocused) onCellFocused(event);
  }, [onCellFocused, refreshRange]);

  const handleCopy = useCallback((event, action = "copied") => {
    const params = gridParamsRef.current;
    if (!params || !copyRangeRef.current || isEditorTarget(event.target)) return;
    const bounds = getRangeBounds(params.api, params.columnApi, copyRangeRef.current);
    if (!bounds) return;

    const copiedRows = [];
    for (let rowIndex = bounds.firstRow; rowIndex <= bounds.lastRow; rowIndex += 1) {
      const rowNode = params.api.getDisplayedRowAtIndex(rowIndex);
      if (!rowNode) continue;
      copiedRows.push(
        bounds.columns
          .slice(bounds.firstColumn, bounds.lastColumn + 1)
          .map(column => formatCopyValue(column, rowNode, params.api, params.columnApi))
          .join("\t")
      );
    }
    if (copiedRows.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    event.clipboardData.setData("text/plain", copiedRows.join("\r\n"));

    const columnCount = bounds.lastColumn - bounds.firstColumn + 1;
    const cellCount = copiedRows.length * columnCount;
    setCopyNotice(cellCount + " " + (cellCount === 1 ? "cell" : "cells") + " " + action);
    if (copyNoticeTimeoutRef.current) clearTimeout(copyNoticeTimeoutRef.current);
    copyNoticeTimeoutRef.current = setTimeout(() => setCopyNotice(""), 1600);
  }, []);

  const handleCut = useCallback(event => {
    const params = gridParamsRef.current;
    if (!params
      || params.api.getEditingCells().length > 0
      || isEditorTarget(event.target)) return;
    handleCopy(event, "cut");
    clearSelectedCells();
  }, [clearSelectedCells, handleCopy]);

  const handlePaste = useCallback(event => {
    const params = gridParamsRef.current;
    const clipboardText = event.clipboardData?.getData("text/plain");
    if (!params || !clipboardText) return;

    const multiCellPaste = clipboardText.includes("\t") || /[\r\n]/.test(clipboardText);
    if (!multiCellPaste && isEditorTarget(event.target)) return;

    const focusedCell = params.api.getFocusedCell();
    if (!focusedCell) return;

    event.preventDefault();
    event.stopPropagation();
    params.api.stopEditing(true);

    const rows = clipboardText
      .replace(/(?:\r?\n)+$/, "")
      .split(/\r?\n/)
      .map(row => row.split("\t"));
    const displayedColumns = params.columnApi.getAllDisplayedColumns();
    let startColumnIndex = displayedColumns.findIndex(column => column === focusedCell.column);
    if (focusedCell.column.getColId() === ROW_NUMBER_COLUMN_ID) {
      startColumnIndex = displayedColumns.findIndex(
        column => column.getColId() !== ROW_NUMBER_COLUMN_ID
      );
    }
    if (startColumnIndex < 0) return;

    rows.forEach((values, rowOffset) => {
      const rowNode = params.api.getDisplayedRowAtIndex(focusedCell.rowIndex + rowOffset);
      if (!rowNode) return;
      values.forEach((rawValue, columnOffset) => {
        const column = displayedColumns[startColumnIndex + columnOffset];
        if (!column || !isEditable(column, rowNode, params.api, params.columnApi)) return;
        const value = parseValue(column, rawValue, rowNode, params.api, params.columnApi);
        rowNode.setDataValue(column, value, "clipboardPaste");
      });
    });

    params.api.refreshCells({ force: true });
  }, []);

  return (
    <div
      ref={gridContainerRef}
      className="community-editable-grid"
      style={{ height: "100%", width: "100%" }}
      onMouseDownCapture={handleGridMouseDown}
      onMouseOverCapture={handleGridMouseOver}
      onKeyDownCapture={handleGridKeyDown}
      onCopyCapture={handleCopy}
      onCutCapture={handleCut}
      onPasteCapture={handlePaste}
    >
      <div
        ref={selectionOutlineRef}
        className="community-selection-outline"
        aria-hidden="true"
      />
      {copyNotice && (
        <div className="community-copy-notice" role="status" aria-live="polite">
          <span aria-hidden="true">✓</span>
          {copyNotice}
        </div>
      )}
      <AgGridReact
        ref={gridRef}
        {...gridProps}
        rowData={rowData}
        columnDefs={enhancedColumnDefs}
        defaultColDef={enhancedDefaultColDef}
        enterNavigatesVertically
        enterNavigatesVerticallyAfterEdit
        onBodyScroll={handleBodyScroll}
        onCellFocused={handleCellFocused}
        onCellMouseDown={handleCellMouseDown}
        onCellMouseOver={handleCellMouseOver}
        onGridReady={handleGridReady}
      />
    </div>
  );
}
